// Resolve RMA codes from names via Official Watts cascade (not ADM InsuranceOffer —
// that view has names only). Used where a downstream still needs codes (Mirror SQL).

import { MCOService } from "components/Insurance/MCOService";

import { pick } from "./mcoShared.js";

// RMA codes are fixed-width zero-padded strings (state=2, county=3, commodity=4). Watts'
// JSON often comes back as bare numbers (e.g. commodityCode: 81, not "0081"), and our own
// Official proxy re-serializes numeric-looking JSON as plain numbers too — pad here, once,
// at the source, so every caller gets the real code instead of a truncated one.
const padCode = (v, width) => (v == null || v === "" ? null : String(v).padStart(width, "0"));

/**
 * @param {object} geo — { reinsurance_year|crop_year, state_name, county_name, commodity_name }
 */
export async function resolveMcoRmaCodes(geo) {
  const crop_year = geo?.crop_year || geo?.reinsurance_year;
  if (!crop_year || !geo?.state_name || !geo?.county_name || !geo?.commodity_name) {
    return { state_code: null, county_code: null, commodity_code: null, practice_code: null };
  }

  const mco = MCOService().actions;
  const states = await mco.selectOfficial("State", { crop_year });
  const state = (states || []).find(
    (s) =>
      String(pick(s, "state_name", "statename") || "").toLowerCase() === geo.state_name.toLowerCase() ||
      String(pick(s, "state_abbreviation", "stateabbreviation") || "").toLowerCase() === geo.state_name.toLowerCase()
  );
  const state_code = padCode(pick(state, "state_code", "statecode"), 2);
  if (!state_code) {
    return { state_code: null, county_code: null, commodity_code: null, practice_code: null };
  }

  const counties = await mco.selectOfficial("County", { crop_year, state_code, state_name: geo.state_name });
  const county = (counties || []).find(
    (c) => String(pick(c, "county_name", "countyname") || "").toLowerCase() === geo.county_name.toLowerCase()
  );
  const county_code = padCode(pick(county, "county_code", "countycode"), 3);
  if (!county_code) {
    return { state_code, county_code: null, commodity_code: null, practice_code: null };
  }

  const commodities = await mco.selectOfficial("Commodity", {
    crop_year,
    state_code,
    county_code,
    state_name: geo.state_name,
    county_name: geo.county_name,
  });
  const commodity = (commodities || []).find(
    (c) =>
      String(pick(c, "commodity_name", "commodityname") || "").toLowerCase() === geo.commodity_name.toLowerCase()
  );
  const commodity_code = padCode(pick(commodity, "commodity_code", "commoditycode"), 4);

  return { state_code, county_code, commodity_code, practice_code: null };
}
